import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, AppBar, Box, Button, Chip, CircularProgress, Container, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack,
  TextField, Toolbar, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TuneIcon from '@mui/icons-material/Tune';
import SegmentIcon from '@mui/icons-material/Segment';
import NotesIcon from '@mui/icons-material/Notes';
import BuildIcon from '@mui/icons-material/Build';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { invoicesAPI } from '../services/api';
import SortableBlockCard from '../components/invoices/SortableBlockCard';
import BlockLibraryDialog from '../components/invoices/BlockLibraryDialog';

const formatEuro = (value) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);

const formatDays = (value) =>
  `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(value || 0)} PT`;

/**
 * Nummerierung der Bausteine -- dieselbe Regel wie im PDF
 * (backend/utils/quote_longform_pdf_generator.py::build_block_tree):
 * Zwischenüberschriften eröffnen ein Kapitel, alles darunter zählt zweistufig.
 */
function numberBlocks(items) {
  const numbers = {};
  let counter = 0;
  let sub = 0;
  let inChapter = false;
  items.forEach((item) => {
    if ((item.kind || 'service') === 'heading') {
      counter += 1; sub = 0; inChapter = true;
      numbers[item.id] = `${counter}.`;
    } else if (inChapter) {
      sub += 1;
      numbers[item.id] = `${counter}.${sub}`;
    } else {
      counter += 1;
      numbers[item.id] = `${counter}.`;
    }
  });
  return numbers;
}

function QuoteLongformPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [templateFor, setTemplateFor] = useState(null);
  const [templateCategory, setTemplateCategory] = useState('');

  // Ein Timer je Baustein: Tippen im Titel darf nicht jeden Tastendruck senden.
  const timers = useRef(new Map());
  const pending = useRef(new Map());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoicesAPI.getById(id);
      setInvoice(res.data);
      setItems(res.data.items || []);
      setTotals({
        total_net: res.data.total_net,
        tax_amount: res.data.tax_amount,
        total_gross: res.data.total_gross,
        total_optional: res.data.total_optional,
        total_days: (res.data.items || [])
          .filter((i) => (i.kind || 'service') === 'service' && !i.is_optional)
          .reduce((sum, i) => sum + Number(i.quantity || 0), 0),
      });
    } catch (err) {
      setError('Angebot konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const applyResponse = useCallback((data) => {
    if (data.invoice_totals) setTotals(data.invoice_totals);
    setItems((prev) => prev.map((i) => (i.id === data.id ? { ...i, ...data } : i)));
  }, []);

  const flushItem = useCallback(async (itemId) => {
    const patch = pending.current.get(itemId);
    if (!patch) return;
    pending.current.delete(itemId);
    const timer = timers.current.get(itemId);
    if (timer) { clearTimeout(timer); timers.current.delete(itemId); }

    setSaveState('saving');
    try {
      const res = await invoicesAPI.updateItem(id, itemId, patch);
      applyResponse(res.data);
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
    }
  }, [id, applyResponse]);

  const flushAll = useCallback(async () => {
    const ids = Array.from(pending.current.keys());
    await Promise.all(ids.map((itemId) => flushItem(itemId)));
  }, [flushItem]);

  // Beim Verlassen der Seite nichts liegen lassen.
  useEffect(() => {
    const handler = (e) => {
      if (pending.current.size > 0) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const patchItem = useCallback((itemId, patch, opts = {}) => {
    // Optimistisch anzeigen, damit die Eingabe nicht ruckelt.
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, ...patch } : i)));
    pending.current.set(itemId, { ...(pending.current.get(itemId) || {}), ...patch });

    const existing = timers.current.get(itemId);
    if (existing) clearTimeout(existing);

    if (opts.flush) { flushItem(itemId); return; }
    const delay = opts.slow ? 1500 : 800;
    timers.current.set(itemId, setTimeout(() => flushItem(itemId), delay));
    setSaveState('saving');
  }, [flushItem]);

  const addBlock = async (kind) => {
    await flushAll();
    try {
      const res = await invoicesAPI.addItem(id, {
        kind,
        description: '',
        quantity: kind === 'service' ? 1 : 0,
      });
      const created = res.data;
      setItems((prev) => [...prev, created]);
      if (created.invoice_totals) setTotals(created.invoice_totals);
    } catch (err) {
      setError('Baustein konnte nicht angelegt werden');
    }
  };

  const insertFromLibrary = async (templateIds) => {
    setLibraryOpen(false);
    await flushAll();
    try {
      const res = await invoicesAPI.addItemsBulk(id, { template_ids: templateIds });
      setItems((prev) => [...prev, ...(res.data.items || [])]);
      if (res.data.invoice_totals) setTotals(res.data.invoice_totals);
    } catch (err) {
      setError('Bausteine konnten nicht eingefügt werden');
    }
  };

  const duplicateBlock = async (item) => {
    await flushAll();
    try {
      const res = await invoicesAPI.addItemsBulk(id, {
        blocks: [{
          kind: item.kind,
          description: item.description,
          body_html: item.body_html,
          quantity: item.quantity,
          is_optional: item.is_optional,
        }],
        after_position: item.position,
      });
      await load();
      if (res.data.invoice_totals) setTotals(res.data.invoice_totals);
    } catch (err) {
      setError('Baustein konnte nicht dupliziert werden');
    }
  };

  const deleteBlock = async (item) => {
    if (!window.confirm('Diesen Baustein löschen?')) return;
    pending.current.delete(item.id);
    try {
      await invoicesAPI.deleteItem(id, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      await load();
    } catch (err) {
      setError('Baustein konnte nicht gelöscht werden');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);   // optimistisch

    try {
      await invoicesAPI.reorderItems(id, reordered.map((i, idx) => ({ id: i.id, position: idx + 1 })));
    } catch (err) {
      setError('Reihenfolge konnte nicht gespeichert werden');
      load();              // Serverstand gewinnt
    }
  };

  const openPreview = async () => {
    await flushAll();
    try {
      const res = await invoicesAPI.previewPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError('Vorschau konnte nicht erzeugt werden');
    }
  };

  const saveAsTemplate = async () => {
    try {
      await invoicesAPI.saveItemAsTemplate(id, templateFor.id, {
        category: templateCategory || null,
      });
      setTemplateFor(null);
      setTemplateCategory('');
    } catch (err) {
      setError('Vorlage konnte nicht gespeichert werden');
    }
  };

  const numbers = useMemo(() => numberBlocks(items), [items]);
  const readOnly = invoice?.status === 'cancelled';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
    );
  }
  if (!invoice) {
    return <Container sx={{ mt: 4 }}><Alert severity="error">{error || 'Nicht gefunden'}</Alert></Container>;
  }

  const saveIndicator = {
    idle: null,
    saving: <Chip size="small" icon={<CloudSyncIcon />} label="Speichert …" />,
    saved: <Chip size="small" color="success" variant="outlined" icon={<CloudDoneIcon />} label="Gespeichert" />,
    error: <Chip size="small" color="error" icon={<ErrorOutlineIcon />} label="Nicht gespeichert"
                 onClick={flushAll} />,
  }[saveState];

  return (
    <Box sx={{ pb: 12 }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={async () => { await flushAll(); navigate('/invoices'); }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {invoice.doc_label || 'Angebot'} {invoice.invoice_number} · {invoice.customer_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {invoice.cover_title || invoice.subject || 'Ohne Titel'}
            </Typography>
          </Box>
          {saveIndicator}
          <Button
            startIcon={<TuneIcon />}
            onClick={async () => {
              // Kunde, Betreff, Gültigkeit, Steuersatz, Tagessatz und AGB liegen
              // im Kopfdaten-Dialog der Liste -- der Editor verwaltet den Inhalt.
              await flushAll();
              navigate('/invoices', { state: { editInvoiceId: Number(id) } });
            }}
          >
            Kopfdaten
          </Button>
          <Button startIcon={<PictureAsPdfIcon />} onClick={openPreview}>Vorschau</Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {items.map((item) => (
              <SortableBlockCard
                key={item.id}
                item={item}
                number={numbers[item.id]}
                disabled={readOnly}
                onPatch={(patch, opts) => patchItem(item.id, patch, opts)}
                onDelete={deleteBlock}
                onDuplicate={duplicateBlock}
                onSaveAsTemplate={(i) => { setTemplateFor(i); setTemplateCategory(''); }}
              />
            ))}
          </SortableContext>
        </DndContext>

        {items.length === 0 && (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            Noch keine Bausteine — unten anfangen.
          </Paper>
        )}
      </Container>

      <Paper
        elevation={3}
        sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0, p: 1.5,
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          borderRadius: 0, zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <Container maxWidth="md" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<BuildIcon />} onClick={() => addBlock('service')}
                  disabled={readOnly}>Leistung</Button>
          <Button size="small" startIcon={<NotesIcon />} onClick={() => addBlock('text')}
                  disabled={readOnly}>Text</Button>
          <Button size="small" startIcon={<SegmentIcon />} onClick={() => addBlock('heading')}
                  disabled={readOnly}>Überschrift</Button>
          <Button size="small" variant="outlined" startIcon={<LibraryBooksIcon />}
                  onClick={(e) => {
                    // Fokus abgeben, sonst liegt er beim Öffnen des Dialogs auf einem
                    // Element, das MUI per aria-hidden ausblendet.
                    e.currentTarget.blur();
                    setLibraryOpen(true);
                  }}
                  disabled={readOnly}>Aus Bibliothek</Button>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={2} alignItems="baseline">
            <Tooltip title="Verbindliche Personentage">
              <Typography variant="body2" color="text.secondary">
                {formatDays(totals?.total_days)}
              </Typography>
            </Tooltip>
            <Typography variant="subtitle1" fontWeight={700}>
              {formatEuro(totals?.total_net)} netto
            </Typography>
            {invoice.doc_variant !== 'sketch' && (
              <Typography variant="body2" color="text.secondary">
                {formatEuro(totals?.total_gross)} brutto
              </Typography>
            )}
            {Number(totals?.total_optional) > 0 && (
              <Typography variant="body2" color="secondary.main">
                + {formatEuro(totals.total_optional)} optional
              </Typography>
            )}
          </Stack>
        </Container>
      </Paper>

      <BlockLibraryDialog
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onInsert={insertFromLibrary}
      />

      <Dialog open={Boolean(templateFor)} onClose={() => setTemplateFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Als Vorlage speichern</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            „{templateFor?.description || 'Ohne Titel'}" wird in die Bibliothek übernommen.
          </Typography>
          <TextField
            autoFocus fullWidth margin="dense" label="Kategorie (optional)"
            value={templateCategory} onChange={(e) => setTemplateCategory(e.target.value)}
            placeholder="z. B. Wartung"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateFor(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={saveAsTemplate}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuoteLongformPage;
